import React from 'react';
import { SelectionControlProps } from '../SelectionControl/types';

import SelectionControl, { SelectionControlRef } from '../SelectionControl';
import { Container } from './styles';
import Spinner from '../Spinner';

type FocusState = 'indie' | 'hover';

interface RenderProps {
  focusState: FocusState;
}

type Props = Partial<
  SelectionControlProps & {
    readOnly: boolean;
    loading: boolean;
    children: React.ReactNode | ((props: RenderProps) => React.ReactNode);
  }
>;

const CheckboxLabel: React.FC<Props> = ({
  onClick,
  className,
  style,
  size = 'normal',
  state = 'unmarked',
  disabled = false,
  readOnly = false,
  loading = false,
  children,
}) => {
  const checkboxRef = React.useRef<SelectionControlRef>(null);
  const [focusState, setFocusState] = React.useState<FocusState>('indie');

  const handleMouseEnter = React.useCallback(() => {
    setFocusState('hover');
    checkboxRef.current?.focus();
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setFocusState('indie');
    checkboxRef.current?.blur();
  }, []);

  const Control = React.useMemo(() => {
    if (loading) {
      return <Spinner type="thick" />;
    }

    return (
      <SelectionControl.Checkbox
        ref={checkboxRef}
        disabled={disabled}
        size={size}
        state={state}
      />
    );
  }, [disabled, loading, size, state]);

  return (
    <Container
      className={className}
      style={style}
      onClick={onClick}
      disabled={disabled || loading}
      readOnly={readOnly}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!readOnly && Control}

      {typeof children === 'function' ? children({ focusState }) : children}
    </Container>
  );
};

export default CheckboxLabel;
